import { useState, useMemo } from "react";

// ─────────────────────────────────────────────────────────────
// Myntra P&L — BUILT from data already in 3June
// Sources: settlement report (commission, logistics, GST, payout),
//          ads (marketing), COGS defaults (product cost).
// No separate "PnL report" needed — 3June assembles it.
//
//  Gross Sales − Returns = Net Sales
//  − Commission − Logistics − TCS/TDS − GST = Bank Settlement
//  Settlement − GST(remitted) = Net Settlement
//  − COGS = CM1 − Packaging = CM2 − Marketing = CM3 (profit)
// ─────────────────────────────────────────────────────────────

const INR  = (n) => "₹" + Math.round(n || 0).toLocaleString("en-IN");
const num  = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

const LS_SETTLE = "3june_myntra_settle";
const LS_ADS    = "3june_myntra_ads";
const LS_COG    = "3june_myntra_cog";
const LS_COSTS  = "3june_myntra_pnl_costs";

const load = (k, d) => { try { return JSON.parse(localStorage.getItem(k) || d); } catch { return JSON.parse(d); } };

const S = {
  card:  { background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden", marginBottom: 16 },
  hdr:   { background: "#0F1A0F", color: "#fff", padding: "13px 18px", fontWeight: 700, fontSize: 14, display: "flex", justifyContent: "space-between", alignItems: "center" },
  row:   { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 18px", fontSize: 13.5, borderBottom: "1px solid #f5f0e8" },
  sub:   { display: "flex", justifyContent: "space-between", padding: "6px 18px 6px 36px", fontSize: 12, color: "#6b7280", borderBottom: "1px solid #faf6ef" },
  lbl:   { color: "#3d3020" },
  neg:   { color: "#dc2626", fontWeight: 600 },
  total: { background: "#fffaf0", fontWeight: 800 },
  input: { width: 120, textAlign: "right", border: "1px solid #e0d5c8", borderRadius: 6, padding: "6px 9px", fontSize: 13, fontFamily: "inherit", background: "#fffdf8", color: "#0F1A0F", fontWeight: 600 },
  cm:    { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", color: "#fff" },
  btn:   (bg = "#F47920") => ({ background: bg, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }),
  th:    { padding: "9px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: "#9ca3af", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" },
  td:    { padding: "9px 12px", borderBottom: "1px solid #f3f4f6", fontSize: 12.5, color: "#374151", whiteSpace: "nowrap" },
};

export default function PageMyntraPnL({ toast }) {
  const settle = load(LS_SETTLE, "[]");
  const ads    = load(LS_ADS, "[]");
  const cogDef = load(LS_COG, "{}");

  // Seller's own costs (editable, saved)
  const [costs, setCosts] = useState(() => load(LS_COSTS, '{"cogsPerUnit":{},"packagingPerUnit":40,"marketing":0}'));
  const saveCosts = (next) => { setCosts(next); localStorage.setItem(LS_COSTS, JSON.stringify(next)); };

  // Seed COGS from existing Myntra COGS defaults the first time
  const cogsFor = (sku) => num(costs.cogsPerUnit[sku]) || num(cogDef[sku]) || 0;

  // ── Build P&L from settlement rows ──
  const pnl = useMemo(() => {
    if (settle.length === 0) return null;

    // A row is a "return" if its return status indicates so
    const isReturn = (r) => /return|rto|cancel/i.test(r.returnStatus || r.revStatus || "");

    let grossSales = 0, returns = 0, commission = 0, logistics = 0, tcs = 0, tds = 0, settlement = 0;
    let soldUnits = 0, returnUnits = 0;
    const skuMap = {};

    settle.forEach((r) => {
      const sale = num(r.customerPaid) || num(r.sellerProductAmt) || num(r.mrp);
      const ret  = isReturn(r);
      grossSales += sale;
      commission += Math.abs(num(r.commission));
      logistics  += Math.abs(num(r.logistic));
      tcs        += Math.abs(num(r.tcs));
      tds        += Math.abs(num(r.tds));
      settlement += num(r.finalSettled);
      if (ret) { returns += sale; returnUnits++; }
      else soldUnits++;

      const sku = r.sku || "—";
      if (!skuMap[sku]) skuMap[sku] = { sku, name: r.styleName || sku, units: 0, sales: 0, settlement: 0 };
      if (!ret) { skuMap[sku].units++; skuMap[sku].sales += sale; }
      skuMap[sku].settlement += num(r.finalSettled);
    });

    const netSales = grossSales - returns;
    // GST ~ extracted from sales (assume 5% on apparel/belts unless we have explicit GST)
    // Settlement already nets most fees; GST remitted is estimated from net sales.
    const gstRate = 0.05;
    const gstRemitted = netSales - netSales / (1 + gstRate); // GST portion inside net sales
    const netSettlement = settlement - gstRemitted;

    // Seller costs
    const cogsTotal = Object.values(skuMap).reduce((a, s) => a + cogsFor(s.sku) * s.units, 0);
    const packaging = num(costs.packagingPerUnit) * soldUnits;
    // Marketing from ads data, or manual override
    const adsTotal = ads.reduce((a, x) => a + num(x.spend), 0);
    const marketing = costs.marketing !== "" && num(costs.marketing) > 0 ? num(costs.marketing) : adsTotal;

    const cm1 = netSettlement - cogsTotal;
    const cm2 = cm1 - packaging;
    const cm3 = cm2 - marketing;
    const cm3Pct = netSales > 0 ? (cm3 / netSales) * 100 : 0;

    return { grossSales, returns, netSales, soldUnits, returnUnits, commission, logistics, tcs, tds,
             settlement, gstRemitted, netSettlement, cogsTotal, packaging, marketing, adsTotal,
             cm1, cm2, cm3, cm3Pct, skuList: Object.values(skuMap).filter((s) => s.units > 0) };
  }, [settle, ads, costs, cogDef]);

  // ── No data state ──
  if (!pnl) {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div style={{ ...S.card, padding: "44px 40px", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 14 }}>📊</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#0F1A0F", marginBottom: 8 }}>Myntra Profit &amp; Loss</div>
          <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.7, marginBottom: 20, maxWidth: 500, margin: "0 auto 20px" }}>
            3June builds your full P&amp;L automatically from the reports you upload — no separate report needed. To see it, first upload your <strong>Myntra Settlement / Payment report</strong> under <strong>Myntra Reports</strong>.
          </div>
          <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "16px 20px", textAlign: "left", maxWidth: 480, margin: "0 auto" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#92670a", textTransform: "uppercase", marginBottom: 10 }}>This P&amp;L combines</div>
            {[
              ["📥 Settlement report", "Gross sales, commission, logistics, TCS/TDS, payout"],
              ["↩️ Returns", "Pulled from settlement return status"],
              ["📣 Ad spend", "From your Myntra Ads report (marketing cost)"],
              ["🏷️ Your COGS", "Product cost per SKU (you enter once)"],
            ].map(([t, d], i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "5px 0", fontSize: 13 }}>
                <span style={{ flexShrink: 0, fontWeight: 600, color: "#3d3020", width: 130 }}>{t}</span>
                <span style={{ color: "#9ca3af" }}>{d}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 18 }}>Go to <strong>Channels → 🛍 Myntra Reports</strong> and upload your settlement file. The P&amp;L appears here automatically.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 920, margin: "0 auto" }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#0F1A0F" }}>📊 Myntra Profit &amp; Loss</div>
        <div style={{ fontSize: 12, color: "#8a7a6a" }}>Built from {settle.length} settlement rows · {pnl.soldUnits} units sold · {pnl.returnUnits} returned</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* LAYER 1 — Settlement */}
        <div style={S.card}>
          <div style={S.hdr}><span>📥 Myntra Settlement</span><span style={{ fontSize: 10, color: "#F47920", textTransform: "uppercase" }}>From your reports</span></div>
          <div style={S.row}><span style={S.lbl}>Gross Sales</span><span>{INR(pnl.grossSales)}</span></div>
          <div style={S.row}><span style={S.lbl}>Returns &amp; Cancellations</span><span style={S.neg}>−{INR(pnl.returns)}</span></div>
          <div style={{ ...S.row, ...S.total }}><span>Net Sales</span><span>{INR(pnl.netSales)}</span></div>
          <div style={S.sub}><span>Commission Fee</span><span style={S.neg}>−{INR(pnl.commission)}</span></div>
          <div style={S.sub}><span>Logistics Charge</span><span style={S.neg}>−{INR(pnl.logistics)}</span></div>
          <div style={S.sub}><span>Taxes (TCS)</span><span style={S.neg}>−{INR(pnl.tcs)}</span></div>
          <div style={S.sub}><span>Taxes (TDS)</span><span style={S.neg}>−{INR(pnl.tds)}</span></div>
          <div style={{ ...S.cm, background: "#1A3A5C" }}>
            <span style={{ fontWeight: 700 }}>Bank Settlement</span>
            <span style={{ fontSize: 20, fontWeight: 700 }}>{INR(pnl.settlement)}</span>
          </div>
        </div>

        {/* LAYER 2 — Real profit */}
        <div style={S.card}>
          <div style={S.hdr}><span>💰 Your Real Profit</span><span style={{ fontSize: 10, color: "#F47920", textTransform: "uppercase" }}>CM1 · CM2 · CM3</span></div>
          <div style={S.row}><span style={S.lbl}>Bank Settlement</span><span>{INR(pnl.settlement)}</span></div>
          <div style={S.row}><span style={S.lbl}>− Product GST <span style={{ fontSize: 11, color: "#9ca3af" }}>(remitted to govt)</span></span><span style={S.neg}>−{INR(pnl.gstRemitted)}</span></div>
          <div style={{ ...S.row, background: "#f9fafb" }}><span style={{ fontWeight: 700 }}>Net Settlement</span><span style={{ fontWeight: 700 }}>{INR(pnl.netSettlement)}</span></div>
          <div style={S.row}><span style={S.lbl}>− Total COGS <span style={{ fontSize: 11, color: "#9ca3af" }}>(product cost)</span></span><span style={S.neg}>−{INR(pnl.cogsTotal)}</span></div>
          <div style={{ ...S.cm, background: "#15803d" }}><span style={{ fontWeight: 700 }}>CM1 <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.8 }}>after product cost</span></span><span style={{ fontSize: 17, fontWeight: 700 }}>{INR(pnl.cm1)}</span></div>
          <div style={S.row}>
            <span style={S.lbl}>− Packaging <span style={{ fontSize: 11, color: "#9ca3af" }}>(₹/unit × {pnl.soldUnits})</span></span>
            <input style={S.input} type="text" inputMode="decimal" value={costs.packagingPerUnit}
              onChange={(e) => saveCosts({ ...costs, packagingPerUnit: e.target.value.replace(/[^0-9.]/g, "") })} />
          </div>
          <div style={{ ...S.cm, background: "#15803d" }}><span style={{ fontWeight: 700 }}>CM2 <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.8 }}>after packaging</span></span><span style={{ fontSize: 17, fontWeight: 700 }}>{INR(pnl.cm2)}</span></div>
          <div style={S.row}>
            <span style={S.lbl}>− Marketing <span style={{ fontSize: 11, color: "#9ca3af" }}>{pnl.adsTotal > 0 && num(costs.marketing) === 0 ? "(from Ads report)" : "(ad spend)"}</span></span>
            <input style={S.input} type="text" inputMode="decimal" placeholder={String(Math.round(pnl.adsTotal))} value={costs.marketing}
              onChange={(e) => saveCosts({ ...costs, marketing: e.target.value.replace(/[^0-9.]/g, "") })} />
          </div>
          <div style={{ ...S.cm, background: pnl.cm3 >= 0 ? "#0f2818" : "#2a0f0f" }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>CM3 — Final Profit</div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>{pnl.cm3Pct.toFixed(1)}% of net sales</div>
            </div>
            <span style={{ fontSize: 26, fontWeight: 800, color: pnl.cm3 >= 0 ? "#4ade80" : "#f87171" }}>{INR(pnl.cm3)}</span>
          </div>
        </div>
      </div>

      {/* Per-SKU */}
      <div style={S.card}>
        <div style={S.hdr}><span>🏷️ Profit per SKU</span><span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>Enter COGS/unit — saved automatically</span></div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["SKU", "Units", "Sales", "Settlement", "COGS/unit", "SKU Profit"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {pnl.skuList.map((s, i) => {
                const cogsU = cogsFor(s.sku);
                const skuProfit = s.settlement - cogsU * s.units;
                return (
                  <tr key={i} style={{ background: i % 2 ? "#fafafa" : "#fff" }}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{s.sku}</td>
                    <td style={S.td}>{s.units}</td>
                    <td style={S.td}>{INR(s.sales)}</td>
                    <td style={S.td}>{INR(s.settlement)}</td>
                    <td style={S.td}>
                      <input style={{ ...S.input, width: 90 }} type="text" inputMode="decimal"
                        value={costs.cogsPerUnit[s.sku] ?? ""} placeholder={String(num(cogDef[s.sku]) || 0)}
                        onChange={(e) => saveCosts({ ...costs, cogsPerUnit: { ...costs.cogsPerUnit, [s.sku]: e.target.value.replace(/[^0-9.]/g, "") } })} />
                    </td>
                    <td style={{ ...S.td, fontWeight: 700, color: skuProfit >= 0 ? "#15803d" : "#dc2626" }}>{INR(skuProfit)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
